import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Layout } from './components/Layout';

// Pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import StudentDetail from './pages/StudentDetail';
import Coaches from './pages/Coaches';
import Programs from './pages/Programs';
import Locations from './pages/Locations';
import Lessons from './pages/Lessons';
import LessonCalendar from './pages/LessonCalendar';
import Reports from './pages/Reports';
import ActivityFeed from './pages/ActivityFeed';
import About from './pages/About';
import Settings from './pages/Settings';
import Manual from './pages/Manual';
import Schools from './pages/Schools';
import SchoolDetail from './pages/SchoolDetail';
import UserManagement from './pages/UserManagement';
import ProgramEnrollment from './pages/ProgramEnrollment';
import VerifyCertificate from './pages/VerifyCertificate';
import CertificatePrint from './pages/CertificatePrint';
import BulkEnrollment from './pages/BulkEnrollment';
import SchoolRegistration from './pages/SchoolRegistration';
import SchoolRegistrations from './pages/SchoolRegistrations';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import HolidayProgram from './pages/HolidayProgram';
import LessonRequestsPage from './pages/LessonRequestsPage';
import FounderPage from './pages/FounderPage';
import FounderEditor from './pages/admin/FounderEditor';
import SocialProofEditor from './pages/admin/SocialProofEditor';
import SafetyPage from './pages/SafetyPage';
import SafetyStandardsEditor from './pages/admin/SafetyStandardsEditor';
import ProgramsPage from './pages/ProgramsPage';
import ProgramsPathwaysEditor from './pages/admin/ProgramsPathwaysEditor';
import PartnershipsPage from './pages/PartnershipsPage';
import PartnershipsEditor from './pages/admin/PartnershipsEditor';
import GlobalPresetsEditor from './pages/admin/GlobalPresetsEditor';
import SEOEditor from './pages/admin/SEOEditor';
import ConversionEditor from './pages/admin/ConversionEditor';
import AnalyticsSimple from './pages/admin/AnalyticsSimple';
import PromoEditor from './pages/admin/PromoEditor';
import StickyConversionBar from './components/StickyConversionBar';
import PromoBanner from './components/PromoBanner';

import '@fontsource/outfit/300.css';
import '@fontsource/outfit/400.css';
import '@fontsource/outfit/500.css';
import '@fontsource/outfit/600.css';
import '@fontsource/outfit/700.css';
import '@fontsource/manrope/300.css';
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/700.css';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, noLayout = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = window.location;
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    // Preserve the current path as redirect URL
    const currentPath = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(currentPath)}`} replace />;
  }

  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Skip layout wrapper for enrollment flow
  if (noLayout) {
    return children;
  }
  
  return <Layout>{children}</Layout>;
};

// Public Route Component (allows access to login/register pages)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const searchParams = new URLSearchParams(window.location.search);
  const hasRedirectParam = searchParams.has('redirect');
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  // If user is authenticated and there's no redirect param, go to dashboard
  // If there IS a redirect param, the Login component will handle the navigation
  if (isAuthenticated && !hasRedirectParam) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Public Certificate Verification */}
      <Route path="/verify/certificate/:certificateId" element={<VerifyCertificate />} />
      
      {/* Certificate Print Page (for PDF generation) */}
      <Route path="/certificate/:certificateId" element={<CertificatePrint />} />
      
      {/* Public School Registration */}
      <Route path="/school-registration" element={<SchoolRegistration />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      
      {/* Holiday Program Landing Page */}
      <Route path="/holiday-program" element={<HolidayProgram />} />
      
      {/* Founder Page */}
      <Route path="/founder" element={<FounderPage />} />
      
      {/* Safety & Programs Public Pages */}
      <Route path="/safety" element={<SafetyPage />} />
      <Route path="/programs" element={<ProgramsPage />} />
      <Route path="/partner-with-us" element={<PartnershipsPage />} />
      
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      
      {/* Program Enrollment (requires auth, no layout) */}
      <Route path="/enroll/:programId" element={<ProtectedRoute noLayout><ProgramEnrollment /></ProtectedRoute>} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/students" element={<ProtectedRoute><Students /></ProtectedRoute>} />
      <Route path="/students/:id" element={<ProtectedRoute><StudentDetail /></ProtectedRoute>} />
      <Route path="/bulk-enrollment" element={<ProtectedRoute><BulkEnrollment /></ProtectedRoute>} />
      <Route path="/coaches" element={<ProtectedRoute><Coaches /></ProtectedRoute>} />
      <Route path="/manage/programs" element={<ProtectedRoute><Programs /></ProtectedRoute>} />
      <Route path="/locations" element={<ProtectedRoute><Locations /></ProtectedRoute>} />
      <Route path="/lessons" element={<ProtectedRoute><Lessons /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><LessonCalendar /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/activity" element={<ProtectedRoute><ActivityFeed /></ProtectedRoute>} />
      <Route path="/about" element={<ProtectedRoute><About /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/manual" element={<ProtectedRoute><Manual /></ProtectedRoute>} />
      <Route path="/schools" element={<ProtectedRoute><Schools /></ProtectedRoute>} />
      <Route path="/schools/:id" element={<ProtectedRoute><SchoolDetail /></ProtectedRoute>} />
      <Route path="/school-registrations" element={<ProtectedRoute><SchoolRegistrations /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute adminOnly><UserManagement /></ProtectedRoute>} />
      <Route path="/admin/lesson-requests" element={<ProtectedRoute adminOnly><LessonRequestsPage /></ProtectedRoute>} />
      <Route path="/admin/founder" element={<ProtectedRoute adminOnly><FounderEditor /></ProtectedRoute>} />
      <Route path="/admin/social-proof" element={<ProtectedRoute adminOnly><SocialProofEditor /></ProtectedRoute>} />
      <Route path="/admin/safety-standards" element={<ProtectedRoute adminOnly><SafetyStandardsEditor /></ProtectedRoute>} />
      <Route path="/admin/programs-pathways" element={<ProtectedRoute adminOnly><ProgramsPathwaysEditor /></ProtectedRoute>} />
      <Route path="/admin/partnerships" element={<ProtectedRoute adminOnly><PartnershipsEditor /></ProtectedRoute>} />
      <Route path="/admin/global-presets" element={<ProtectedRoute adminOnly><GlobalPresetsEditor /></ProtectedRoute>} />
      <Route path="/admin/seo" element={<ProtectedRoute adminOnly><SEOEditor /></ProtectedRoute>} />
      <Route path="/admin/conversion" element={<ProtectedRoute adminOnly><ConversionEditor /></ProtectedRoute>} />
      <Route path="/admin/analytics-simple" element={<ProtectedRoute adminOnly><AnalyticsSimple /></ProtectedRoute>} />
      <Route path="/admin/promo" element={<ProtectedRoute adminOnly><PromoEditor /></ProtectedRoute>} />
      
      {/* Fallback - redirect to landing page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App">
          <AppRoutes />
          <PromoBanner />
          <StickyConversionBar />
          <Toaster position="top-right" richColors />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
